import { useCallback, useEffect, useRef } from 'react';

import { hide, onHide, show } from '@intercom/messenger-js-sdk';

import { useConfig } from '@/config/ConfigProvider';

export interface IUseIntercomMessengerResult {
  /** False when no Intercom app id is configured for this environment. */
  isConfigured: boolean;
  /** Opens the messenger, or closes it if this opened it. */
  toggle: () => void;
}

/**
 * Owns the Intercom messenger's open/closed state for the Help entry.
 *
 * Intercom is an imperative third-party SDK with no state to read back, so the
 * open state is tracked here and re-synced from the messenger's own close
 * button via `onHide`.
 *
 * @returns Whether Intercom is available, and a toggle for its messenger.
 */
export function useIntercomMessenger(): IUseIntercomMessengerResult {
  const config = useConfig();
  const isConfigured = Boolean(config.intercomAppId);
  const isOpenRef = useRef(false);

  // eslint-disable-next-line no-restricted-syntax -- reason: Intercom is a third-party imperative SDK; sync our close state from its own UI
  useEffect(() => {
    if (!isConfigured) {
      return;
    }

    // Intercom's `onHide` has no deregister API; left uncleaned intentionally
    // since the callback only resets an orphaned ref (idempotent no-op).
    onHide(() => {
      isOpenRef.current = false;
    });
  }, [isConfigured]);

  const toggle = useCallback(() => {
    if (isOpenRef.current) {
      hide();
      isOpenRef.current = false;
      return;
    }

    show();
    isOpenRef.current = true;
  }, []);

  return { isConfigured, toggle };
}
